<script>
export const group = 'Full'
</script>

<script setup>
import { frames } from '../../../utils/frames'
import InformationSurface from './shared/InformationSurface.vue'
import Reveal from './shared/Reveal.vue'
const gsapTimeline = useGsapTimeline()

const { item } = defineProps({
  item: Object,
})

const panelElement = ref(null)
const titleElement = ref(null)
const subtitleElement = ref(null)
const todaySectionElement = ref(null)
const laterSectionElement = ref(null)
const confettiElements = ref([])

const users = computed(() =>
  (item.enrichment?.users || []).map((user) => ({
    ...user,
    nextBirthday: new Date(user.next_birthday + 'T00:00:00'),
  }))
)

const startOfToday = new Date(new Date().toDateString())
const diffDays = (date) => Math.round((date - startOfToday) / (1000 * 60 * 60 * 24))

const isBirthdayToday = (user) => diffDays(user.nextBirthday) === 0

const todayUsers = computed(() => users.value.filter((user) => isBirthdayToday(user)))
const laterUsers = computed(() => users.value.filter((user) => !isBirthdayToday(user)))

const formatDay = (date) => {
  const diff = diffDays(date)
  if (diff === 0) {
    return 'vandaag'
  }
  if (diff === 1) {
    return 'morgen'
  }
  return new Intl.DateTimeFormat('nl-NL', { weekday: 'long' }).format(date)
}

const laterUsersByDay = computed(() =>
  laterUsers.value.reduce((groups, user) => {
    const last = groups[groups.length - 1]
    if (last && last.day === user.next_birthday) {
      last.users.push(user)
    } else {
      groups.push({ day: user.next_birthday, label: formatDay(user.nextBirthday), users: [user] })
    }
    return groups
  }, [])
)

onMounted(() => {
  gsapTimeline({ name: 'InternalUpcomingBirthdays', delay: frames(10, 50) })
    .from(panelElement, { y: 36, scale: 0.98, opacity: 0, duration: frames(55, 50), ease: 'power3.out' })
    .from([titleElement, subtitleElement], { y: 56, opacity: 0, duration: frames(64, 50), ease: 'power3.out' }, `<${frames(3, 50)}`)
    .from(todaySectionElement.value.children, { y: 40, scale: 0.9, opacity: 0, duration: frames(40, 50), ease: 'power3.out', stagger: frames(3, 50) }, `<${frames(3, 50)}`)
    .from(laterSectionElement.value?.children, { x: -30, opacity: 0, duration: frames(42, 50), ease: 'power2.out', stagger: frames(2, 50) }, `<${frames(2, 50)}`)

  confettiElements.value.forEach((el, i) => {
    gsapTimeline({ name: `Confetti-${i}`, repeat: -1, yoyo: true, delay: i * 0.3 }).to(el, {
      y: -20 + Math.random() * 40,
      x: -15 + Math.random() * 30,
      rotate: -30 + Math.random() * 60,
      duration: 2 + Math.random() * 2,
      ease: 'sine.inOut',
    })
  })
})
</script>

<template>
  <Reveal>
    <InformationSurface>
      <div ref="panelElement" class="flex size-full flex-col">
        <!-- Floating confetti decorations -->
        <div :ref="(el) => (confettiElements[0] = el)" class="absolute right-16 top-8 text-5xl rotate-12 opacity-70">🎈</div>
        <div :ref="(el) => (confettiElements[1] = el)" class="absolute right-48 top-24 text-4xl -rotate-6 opacity-60">🎉</div>
        <div :ref="(el) => (confettiElements[2] = el)" class="absolute right-8 top-1/3 text-5xl rotate-[-20deg] opacity-50">🎊</div>
        <div :ref="(el) => (confettiElements[3] = el)" class="absolute left-4 bottom-24 text-4xl rotate-[15deg] opacity-50">✨</div>
        <div :ref="(el) => (confettiElements[4] = el)" class="absolute right-32 bottom-16 text-5xl rotate-6 opacity-60">🎂</div>

        <div class="flex min-h-0 flex-1 flex-col">
          <div class="relative mb-8 flex items-baseline">
            <h1 ref="titleElement" class="font-cervo text-7xl leading-[0.92] tracking-[-0.03em] text-white font-bold relative z-10">🎂 Vandaag Jarig</h1>
            <h2 ref="subtitleElement" class="ml-4 text-3xl tracking-[0.08em]">Powered by Dario</h2>
            <div class="absolute right-0 top-4 h-12 w-12 rounded-full bg-q-cr-red-primary/50" />
          </div>

          <!-- Today: 2x2 grid, or 3x2 if more than 4 -->
          <div v-if="todayUsers.length" ref="todaySectionElement" class="grid flex-1 gap-5" :class="todayUsers.length > 4 ? 'grid-cols-3 grid-rows-2' : 'grid-cols-2 grid-rows-2'">
            <div
              v-for="user in todayUsers"
              :key="user.id"
              class="flex flex-col items-center justify-center rounded-3xl border-3 border-q-cr-red-primary/70 bg-gradient-to-br from-q-cr-red-primary/35 via-q-cr-red-primary/20 to-joe-cr-blue-primary/25 p-6 shadow-[0_0_40px_rgba(237,54,36,0.3)] backdrop-blur-sm"
            >
              <div class="text-5xl mb-3">🎉</div>
              <span class="font-sans text-5xl font-black text-white text-center leading-tight text-balance">{{ user.name }}</span>
              <span class="font-rift text-3xl font-semibold uppercase tracking-wider text-q-cr-red-primary mt-2">vandaag 🎂</span>
            </div>
          </div>
          <div v-else ref="todaySectionElement" class="h-full">
            <div
              class="h-full flex flex-col items-center justify-center py-32 rounded-3xl border-3 border-q-cr-red-primary/40 bg-gradient-to-br from-q-cr-red-primary/15 via-q-cr-red-primary/5 to-joe-cr-blue-primary/10 p-6 shadow-[0_0_40px_rgba(237,54,36,0.3)] backdrop-blur-sm"
            >
              <div class="text-5xl mb-3">😌</div>
              <span class="font-qarla text-4xl font-bold text-white/60 text-center leading-tight">Vandaag is er niemand jarig</span>
            </div>
          </div>

          <div class="mt-auto pt-8 flex items-center gap-5">
            <div class="h-8 w-8 rounded-full bg-gradient-to-br from-willy-cr-gray-primary/10 to-willy-cr-black-primary border border-willy-cr-gray-primary/20" />
            <div class="h-3 w-32 rounded-full bg-q-cr-red-primary/60" />
            <div class="h-8 w-8 rounded-full bg-gradient-to-br from-q-cr-red-primary to-q-cr-red-primary/80" />
            <div class="h-3 w-40 rounded-full bg-joe-cr-blue-primary/60" />
            <div class="h-8 w-8 rounded-full bg-gradient-to-br from-joe-cr-blue-primary to-joe-cr-blue-primary/80" />
          </div>
        </div>
      </div>

      <template #side>
        <div class="flex h-full flex-col overflow-hidden">
          <p class="font-rift text-5xl font-bold uppercase tracking-[0.08em] text-white/90 mb-5">Later deze week</p>
          <div v-if="laterUsers.length" ref="laterSectionElement" class="flex flex-col gap-4 overflow-y-auto">
            <div v-for="dayGroup in laterUsersByDay" :key="dayGroup.day">
              <p class="font-rift text-4xl font-semibold uppercase tracking-[0.06em] text-white/50 mb-4">{{ dayGroup.label }}</p>
              <div class="flex flex-col gap-3">
                <div v-for="user in dayGroup.users" :key="user.id" class="flex items-center rounded-2xl bg-white/[0.06] border border-white/10 px-6 py-5 backdrop-blur-sm">
                  <span class="font-sans text-3xl font-bold text-white truncate">{{ user.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </InformationSurface>
  </Reveal>
</template>
